/**
 * WebSocket hook for real-time test progress updates
 *
 * Connects to tester server WebSocket, handles reconnection on disconnect,
 * and dispatches parsed messages to callback.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { TESTER_BASE_URL } from "../api/versionTesterApi";
import type { WsMessage } from "../types";

/**
 * Derive WebSocket URL from HTTP base URL
 * Replaces http:// with ws:// and https:// with wss://
 */
const getWebSocketUrl = (baseUrl: string): string => {
  return baseUrl
    .replace(/^http:\/\//, "ws://")
    .replace(/^https:\/\//, "wss://");
};

const WS_URL = getWebSocketUrl(TESTER_BASE_URL);

interface UseVersionTesterSocketOptions {
  onMessage?: (msg: WsMessage) => void;
  enabled?: boolean;
}

interface UseVersionTesterSocketResult {
  connected: boolean;
  lastMessage: WsMessage | null;
}

/**
 * Hook to connect to tester server WebSocket
 *
 * Features:
 * - Automatic reconnection on disconnect (3s delay)
 * - Stable implementation (no infinite re-render loops)
 * - Clean cleanup on unmount
 * - Optional message callback
 *
 * @param options.onMessage - Optional callback for each message received
 * @param options.enabled - Whether to connect (default true)
 * @returns { connected, lastMessage }
 */
export const useVersionTesterSocket = ({
  onMessage,
  enabled = true,
}: UseVersionTesterSocketOptions = {}): UseVersionTesterSocketResult => {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WsMessage | null>(null);

  // Use refs to avoid recreating effect on callback changes
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage callback ref current
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    // Don't connect if disabled
    if (!enabled) {
      return;
    }

    const connect = () => {
      // Clean up existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;

        ws.addEventListener("open", () => {
          setConnected(true);
          console.log("[VersionTesterSocket] Connected to tester server");
        });

        ws.addEventListener("close", () => {
          setConnected(false);
          console.log("[VersionTesterSocket] Disconnected from tester server");

          // Schedule reconnection after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("[VersionTesterSocket] Attempting reconnection...");
            connect();
          }, 3000);
        });

        ws.addEventListener("message", (event) => {
          try {
            const msg = JSON.parse(event.data) as WsMessage;
            setLastMessage(msg);

            // Call optional callback
            if (onMessageRef.current) {
              onMessageRef.current(msg);
            }
          } catch (error) {
            console.error(
              "[VersionTesterSocket] Failed to parse message:",
              error,
            );
          }
        });

        ws.addEventListener("error", (error) => {
          console.error("[VersionTesterSocket] WebSocket error:", error);
        });
      } catch (error) {
        console.error(
          "[VersionTesterSocket] Failed to create WebSocket:",
          error,
        );
      }
    };

    // Initial connection
    connect();

    // Cleanup on unmount or when enabled changes
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      setConnected(false);
    };
  }, [enabled]); // Only re-run if enabled changes

  return { connected, lastMessage };
};
